import React from 'react'
import classNames from 'classnames'
import { motion } from 'motion/react'

import type { Icon } from '@slite/icons'
import { CheckMarkRoundIcon, CloseIcon } from '@slite/icons'

import type { ButtonProps } from '../Button/Button'
import { Button } from '../Button/Button'
import type { ButtonHTMLLinkProps } from '../Button/ButtonHTMLLink'
import { ButtonHTMLLink } from '../Button/ButtonHTMLLink'
import type { ButtonLinkProps } from '../Button/ButtonLink'
import { ButtonLink } from '../Button/ButtonLink'
import type { ButtonVariant } from '../Button/constants'
import { IconButton } from '../IconButton/IconButton'
import { Text } from '../Text/Text'

type SnackbarVariant = 'info' | 'danger' | 'purple'

export type SnackbarProps = {
  variant?: SnackbarVariant
  icon?: Icon
  children: React.ReactNode
  action?: SnackbarActionProps
  secondaryAction?: SnackbarActionProps
  onClose?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  radiusSize?: 'normal' | 'large' | 'xlarger'
  className?: string
  style?: React.CSSProperties
}

type SnackbarActionBaseProps = {
  content: ButtonProps['children']
}

type SnackbarActionOnClick = ({
  event,
  onCloseToaster,
}: {
  event: React.MouseEvent<HTMLElement>
  onCloseToaster?: () => void
}) => void

type SnackbarActionProps =
  // Button
  | (SnackbarActionBaseProps & {
      onClick: SnackbarActionOnClick
      to?: never
      href?: never
    })
  // ButtonLink
  | (SnackbarActionBaseProps & {
      onClick?: SnackbarActionOnClick
      to: ButtonLinkProps['to']
      href?: never
    })
  // ButtonHTMLLink
  | (SnackbarActionBaseProps & {
      onClick?: SnackbarActionOnClick
      to?: never
      href: ButtonHTMLLinkProps['href']
    })

const SnackbarActionButton = ({
  to,
  href,
  children,
  onClick,
  onCloseToaster,
  variant,
}: Omit<SnackbarActionProps, 'content'> & {
  onCloseToaster?: () => void
  children: React.ReactNode
  variant: ButtonVariant
}) => {
  if (to) {
    return (
      <ButtonLink
        size="small"
        variant={variant}
        to={to}
        onClick={(event) => onClick?.({ event, onCloseToaster })}
        transparent
      >
        {children}
      </ButtonLink>
    )
  }

  if (href) {
    return (
      <ButtonHTMLLink
        href={href}
        size="small"
        variant={variant}
        rel="noopener noreferrer"
        target="_blank"
        onClick={(event) => onClick?.({ event, onCloseToaster })}
        transparent
      >
        {children}
      </ButtonHTMLLink>
    )
  }

  return (
    <Button
      size="small"
      variant={variant}
      onClick={(event) => onClick?.({ event, onCloseToaster })}
      transparent
    >
      {children}
    </Button>
  )
}

const snackbarClassNamesFromVariant: Record<
  SnackbarVariant,
  { container: string; content: string }
> = {
  info: {
    container: 'background-blue-70 border-alpha-blue-40',
    content: 'blue-15',
  },
  danger: {
    container:
      'background-red-80 border-alpha-red-45 background-alpha-red-45-on-dark-mode',
    content: 'red-30',
  },
  purple: {
    container: 'background-alpha-purple-50 border-alpha-purple-40',
    content: 'purple-20',
  },
}

export const Snackbar = ({
  icon: Icon = CheckMarkRoundIcon,
  children,
  action,
  secondaryAction,
  variant = 'info',
  onClose,
  radiusSize = 'xlarger',
  className,
  ...props
}: SnackbarProps) => {
  let buttonVariant = variant as ButtonVariant
  if (variant === 'purple') {
    buttonVariant = 'secondary-purple'
  }
  if (variant === 'info') {
    buttonVariant = 'secondary-blue'
  }
  if (variant === 'danger') {
    buttonVariant = 'destructive'
  }
  return (
    <motion.div
      {...props}
      className={classNames(
        'flexbox flexbox-align-center flexbox-gap-12',
        'padding-vertical-12 padding-horizontal-16 border-small',
        `radius-${radiusSize}`,
        snackbarClassNamesFromVariant[variant].container,
        className
      )}
    >
      <div className="flexbox flexbox-align-center  flexbox-gap-12">
        <Icon
          size={16}
          className={classNames(
            'flexbox-no-shrink',
            snackbarClassNamesFromVariant[variant].content
          )}
        />
        <Text
          variant="body"
          className={classNames(
            'font-weight-semibold flexbox-grow break-word',
            snackbarClassNamesFromVariant[variant].content
          )}
        >
          {children}
        </Text>
      </div>
      {(secondaryAction || action || onClose) && ( //we have a flexbox gap above, so rendering an empty div can create empty space
        <div className="flexbox flexbox-gap-4 flexbox-justify-end flexbox-wrap margin-left-auto">
          {secondaryAction && (
            <SnackbarActionButton
              to={secondaryAction.to}
              href={secondaryAction.href}
              onCloseToaster={onClose}
              onClick={secondaryAction.onClick}
              variant={buttonVariant}
            >
              {secondaryAction.content}
            </SnackbarActionButton>
          )}
          {action && (
            <SnackbarActionButton
              to={action.to}
              href={action.href}
              onClick={action.onClick}
              onCloseToaster={onClose}
              variant={buttonVariant}
            >
              {action.content}
            </SnackbarActionButton>
          )}
          {onClose && (
            <IconButton
              data-test-id="snackbarCloseButton"
              variant="tertiary"
              size="small"
              iconSize="small"
              icon={CloseIcon}
              onClick={onClose}
            />
          )}
        </div>
      )}
    </motion.div>
  )
}
